/**
 * Histórico de impressão (vindo do ERP) aplicado aos lotes da planilha:
 * selos NOVO / IMPRESSO / REOTIMIZADO, filtros rápidos, busca e nomes dos
 * arquivos exportados.
 *
 * O histórico é por lote + setor (`/api/apontamentos/resumo`). REOTIMIZADO:
 * a data de otimização na planilha ficou POSTERIOR à que estava na última
 * impressão, então o papel no quadro está desatualizado.
 */
import { pontuar } from "../buscaTolerante";
import { somarDiasCorridos } from "../capacidade/calendario";
import { entregaDoLote } from "./lotes";

export const SELO = Object.freeze({ NOVO: "novo", IMPRESSO: "impresso", REOTIMIZADO: "reotimizado" });

export const FILTROS = Object.freeze([
  { id: "nao_impressos", rotulo: "Não impressos" },
  { id: "ultimos_7_dias", rotulo: "Otimizados nos últimos 7 dias" },
  { id: "em_producao", rotulo: "Em produção" },
  { id: "todos", rotulo: "Todos" },
]);
export const FILTRO_PADRAO = "nao_impressos";

const DIAS_DO_FILTRO_RECENTE = 7;

/** Agrupa os itens do resumo por lote: { chave → { setor → { vias, ultimaEm, ultimaOtimizacao } } }. */
export function indexarResumo(itens) {
  const porLote = new Map();
  (itens || []).forEach((item) => {
    if (!porLote.has(item.lote_chave)) porLote.set(item.lote_chave, {});
    porLote.get(item.lote_chave)[item.setor] = {
      vias: item.vias,
      ultimaEm: item.ultima_em,
      ultimaOtimizacao: item.ultima_otimizacao || null,
    };
  });
  return porLote;
}

function maisRecente(registrosDoLote) {
  return Object.values(registrosDoLote || {}).reduce(
    (atual, registro) => (!atual || registro.ultimaEm > atual.ultimaEm ? registro : atual),
    null,
  );
}

/**
 * { tipo, em?, otimizacaoAnterior? }. `em` é o ISO da impressão mais recente
 * de qualquer setor do lote.
 */
export function seloDoLote(lote, resumoPorLote) {
  const ultima = maisRecente(resumoPorLote?.get(lote.chave));
  if (!ultima) return { tipo: SELO.NOVO };
  const anterior = ultima.ultimaOtimizacao;
  if (anterior && lote.otimizacao && lote.otimizacao > anterior) {
    return { tipo: SELO.REOTIMIZADO, em: ultima.ultimaEm, otimizacaoAnterior: anterior };
  }
  return { tipo: SELO.IMPRESSO, em: ultima.ultimaEm };
}

/** A via que a próxima impressão deste lote+setor vai ter (1 = original). */
export function proximaVia(lote, setorId, resumoPorLote) {
  const registro = resumoPorLote?.get(lote.chave)?.[setorId];
  return (registro?.vias || 0) + 1;
}

/**
 * Filtros rápidos. "Não impressos" (o padrão) só olha lotes em aberto: na
 * estreia não há histórico e os lotes já carregados não podem aparecer como
 * NOVO (decisão do Igor, 22/10). REOTIMIZADO conta como não impresso.
 */
export function filtrarLotes(lotes, filtro, { resumoPorLote, hoje }) {
  switch (filtro) {
    case "nao_impressos":
      return lotes.filter((lote) => lote.emAberto && seloDoLote(lote, resumoPorLote).tipo !== SELO.IMPRESSO);
    case "ultimos_7_dias": {
      const desde = somarDiasCorridos(hoje, -DIAS_DO_FILTRO_RECENTE);
      return lotes.filter((lote) => lote.otimizacao >= desde);
    }
    case "em_producao":
      return lotes.filter((lote) => lote.emAberto && (entregaDoLote(lote) || "") >= hoje);
    case "todos":
      return lotes;
    default:
      throw new TypeError(`Filtro desconhecido: ${String(filtro)}.`);
  }
}

/** Busca por cliente ou número do lote. Número procura pelo começo ("50" acha 508). */
export function buscarLotes(lotes, consulta) {
  const texto = String(consulta || "").trim();
  if (!texto) return lotes;
  if (/^\d+$/.test(texto)) return lotes.filter((lote) => lote.numero.startsWith(texto));
  return lotes.filter((lote) => pontuar(texto, `${lote.clientes.join(" ")} ${lote.rotulo}`) > 0);
}

/** Lotes agrupados por cliente, na ordem em que os lotes chegaram. */
export function agruparPorCliente(lotes) {
  const grupos = new Map();
  lotes.forEach((lote) => {
    if (!grupos.has(lote.cliente)) grupos.set(lote.cliente, []);
    grupos.get(lote.cliente).push(lote);
  });
  return [...grupos.entries()].map(([cliente, doCliente]) => ({ cliente, lotes: doCliente }));
}

// Nome de arquivo: sem acento, sem caractere proibido no Windows, com espaços.
function paraArquivo(texto) {
  return String(texto)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[\\/:*?"<>|·]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function descreverLote(lote) {
  return `Lote ${paraArquivo(lote.numero)} - ${paraArquivo(lote.cliente).toUpperCase()}`;
}

/**
 * "Apontamentos - Lote 508 - PAULO EDUARDO GALVAO - 2026-10-22.pdf" ou
 * "Apontamentos - 3 lotes - 2026-10-22.pdf".
 */
export function nomeDoArquivo({ lotes, hoje, extensao, detalhe }) {
  const quem = lotes.length === 1 ? descreverLote(lotes[0]) : `${lotes.length} lotes`;
  const partes = ["Apontamentos", quem, detalhe && paraArquivo(detalhe), hoje].filter(Boolean);
  return `${partes.join(" - ")}.${extensao}`;
}
